import { isImageDatasetType, readDatasetMeta } from '@/shared/bfSeller';
import type { DataSetsProps, Tables } from '@/types';


// Types
type DownloadMemProfile = {
  catalogChunkSize: number;
  clientsChunkSize: number;
  maxConcurrentDownloads: number;
};

export type DatasetWithStoreMeta = DataSetsProps & {
  COD_LOJA?: number | string | null;
  COD_SEGMENTO?: number | string | null;
};



// Constants
const DATASET_TYPE_MAP: Record<string, string> = {
  imagens_zip: 'imagens',
  // Nome novo do arquivo de ciclo de venda. A troca no backend é gradual e os dois têm o mesmo
  // conteúdo: normalizar aqui faz `__meta`, `temDado`, relatório e leitura verem um tipo só.
  ciclo_venda_cliente: 'ciclo_venda_cliente_cliente',
};

const DEFAULT_MEM_PROFILE: DownloadMemProfile = {
  catalogChunkSize: 400,
  clientsChunkSize: 300,
  maxConcurrentDownloads: 2,
};

export const PROGRESS_THROTTLE_MS = 150;

export const SPEED_UPDATE_INTERVAL_MS = 500;

export const progressLastCall: Record<string, number> = {};

export const progressPercentSetterRef: { current: (n: number) => void } = { current: () => {} };

export const _memTune = getDownloadMemoryTuning();


// Functions
function getDownloadMemoryTuning(): DownloadMemProfile {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('pwa_sync_low_memory') === '1') {
      return {
        catalogChunkSize: 150,
        clientsChunkSize: 120,
        maxConcurrentDownloads: 1,
      };
    }
  } catch {
    /* storage indisponível */
  }

  if (typeof navigator === 'undefined') return DEFAULT_MEM_PROFILE;

  const gb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (gb !== undefined && gb <= 2) {
    return {
      catalogChunkSize: 150,
      clientsChunkSize: 120,
      maxConcurrentDownloads: 1,
    };
  }
  if (gb !== undefined && gb <= 4) {
    return {
      catalogChunkSize: 280,
      clientsChunkSize: 220,
      maxConcurrentDownloads: 1,
    };
  }
  return DEFAULT_MEM_PROFILE;
}

export const normalizeDatasetType = (tipo: string) => DATASET_TYPE_MAP[tipo] || tipo;

export const updateProgress = (loaded: number, total: number, name: string) => {
  const now = Date.now();
  if (progressLastCall[name] != null && now - progressLastCall[name] < PROGRESS_THROTTLE_MS) return;
  progressLastCall[name] = now;

  const percentual = total > 0 ? Math.min((loaded / total) * 100, 100) : 0;
  const progressBar = document.getElementById(`${name}-bar`) as HTMLElement;
  const progressText = document.getElementById(`${name}-text`) as HTMLElement;
  const isValid = Number.isFinite(percentual);

  if (name === 'progress') {
    progressPercentSetterRef.current(percentual);
  } else {
    if (progressBar) progressBar.style.width = `${percentual}%`;
  }
  if (progressText) progressText.textContent = isValid ? `${Math.round(percentual)}%` : 'Erro no Download';
};

export const getDatasetKey = (dataset: DatasetWithStoreMeta, fallbackIndex?: number) =>
  [
    normalizeDatasetType(dataset.TIPO_ARQUIVO),
    dataset.COD_LOJA ?? 'null',
    dataset.COD_SEGMENTO ?? 'null',
    dataset.URL_S3 || fallbackIndex || 'no-url',
  ].join('::');
// -------------------------------------------------------------------------------------------
// identidade do arquivo e o que já foi baixado
// -------------------------------------------------------------------------------------------

/**
 * Params que carregam só a assinatura do S3.
 *
 * O `getdataset` devolve link pré-assinado: data, prazo e assinatura novas a cada chamada, sem o
 * arquivo ter mudado. Comparar a URL inteira faria todo arquivo parecer novo em todo sync.
 */
const AUTH_QUERY_PARAM = /^(x-amz-|amz-sdk-)|^(awsaccesskeyid|signature|expires|token)$/i;

/** URL sem os params de assinatura: o que sobra é o arquivo na origem. */
export const urlSemAutenticacao = (url: string): string => {
  const [semFragmento] = String(url || '').split('#');
  const [base, ...resto] = semFragmento.split('?');
  const query = resto.join('?');
  if (!query) return base;

  const mantidos = query
    .split('&')
    .filter(Boolean)
    .filter((par) => {
      const nome = par.split('=')[0] || '';
      try {
        return !AUTH_QUERY_PARAM.test(decodeURIComponent(nome));
      } catch {
        return !AUTH_QUERY_PARAM.test(nome);
      }
    })
    .sort();

  return mantidos.length ? `${base}?${mantidos.join('&')}` : base;
};

/**
 * Identidade de um arquivo de dataset: tipo + arquivo na origem + versão publicada.
 *
 * É o que decide se um link novo é um arquivo novo. A assinatura fica de fora (ver
 * `AUTH_QUERY_PARAM`) e o `DT_UPDATE` entra porque o arquivo é republicado no mesmo caminho: só
 * ele separa a versão de hoje da de ontem.
 */
export const identidadeArquivo = (tipo: string, url: string, dtUpdate?: string | null): string =>
  `${normalizeDatasetType(tipo)}::${urlSemAutenticacao(url)}::${dtUpdate ?? ''}`;

/**
 * O que já está baixado, a partir de **uma** leitura do `__meta` por sync.
 *
 * O `__meta` é a única fonte: ele guarda, por arquivo, a identidade (URL e `DT_UPDATE`), o
 * `status` e quantos itens entraram — e vive no mesmo banco que o conteúdo, então não tem como um
 * dizer "baixado" e o outro estar vazio.
 *
 * Duas perguntas diferentes saem daqui:
 *  - `temDado(tipo)`: o dataset tem conteúdo gravado (é o que bloqueia o sync das tabelas
 *    obrigatórias). Para imagens, quem responde é a contagem no `BF_SELLER_IMAGES`;
 *  - `foiBaixado(tipo, url, dtUpdate)`: **aquele arquivo, nesta versão**, foi baixado com sucesso.
 *    Não há prazo de validade: registro antigo de arquivo que não mudou continua valendo.
 */
export const buildDownloadState = async (validateTable: (table: Tables) => Promise<boolean>) => {
  const [entries, temImagens] = await Promise.all([readDatasetMeta(), validateTable('imagens')]);

  const comDado = new Set<string>();
  const baixados = new Set<string>();

  for (const entry of entries) {
    if (entry?.status !== 'ok' || !(Number(entry?.rows) > 0)) continue;

    if (entry.tipo) comDado.add(entry.tipo);
    if (entry.store) comDado.add(entry.store);
    // Registro gravado antes desta regra guarda a URL assinada; a normalização iguala os dois.
    if (entry.url) baixados.add(identidadeArquivo(entry.tipo, entry.url, entry.dtUpdate));
  }

  return {
    temDado: (tipo: string): boolean => (isImageDatasetType(tipo) ? temImagens : comDado.has(tipo)),
    foiBaixado: (tipo: string, url: string, dtUpdate?: string | null): boolean =>
      baixados.has(identidadeArquivo(tipo, url, dtUpdate)),
  };
};
